import {
  AfterViewInit,
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChangeCallback, TouchedCallback } from '@fmnts/common/forms';
import { clamp } from '@fmnts/core/math';
import {
  CursorSelection,
  noopTransform,
  UpdateCursorOptions,
  ValueTransform,
} from './value-transform';

/**
 * Directive to use on an input element that can be used to
 * programmatically transform between a "value" which is the
 * representation in the `FormControl` and a "display value"
 * which is the value visible to the user in the input.
 *
 * It can be used to offer formatting to an input element
 * e.g. when using masked inputs.
 */
@Directive({
  selector: 'input[fmntsValueTransform]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ValueTransformDirective),
      multi: true,
    },
  ],
})
export class ValueTransformDirective
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('fmntsValueTransform')
  get valueTransform(): ValueTransform<string, unknown> {
    return this._valueTransform;
  }
  set valueTransform(value: ValueTransform<string, any> | undefined) {
    this._valueTransform =
      value ?? (noopTransform as ValueTransform<string, unknown>);
  }

  private _valueTransform: ValueTransform<string, unknown> =
    noopTransform as ValueTransform<string, unknown>;

  /** Previous cursor selection */
  private previousCursor: CursorSelection | null = null;

  private _value: unknown;

  private _onChange: ChangeCallback<unknown> = () => {};
  private _onTouched: TouchedCallback = () => {};

  constructor(private element: ElementRef<HTMLInputElement>) {}

  private set value(value: unknown) {
    this._value = value;
    this._onChange(this._value);
  }

  ngOnInit(): void {
    this.updateDisplayValue();
  }

  ngAfterViewInit(): void {
    this.previousCursor = this.getCursorSelection(this.element.nativeElement);
  }

  writeValue(value: unknown): void {
    this._value = value;
    this.updateDisplayValue();
  }

  registerOnChange(fn: ChangeCallback): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: TouchedCallback): void {
    this._onTouched = fn;
  }

  @HostListener('mousedown', ['$event'])
  @HostListener('keydown', ['$event'])
  @HostListener('touchstart', ['$event'])
  @HostListener('focus', ['$event'])
  onInteractionStart(e: Event): void {
    this.previousCursor = this.getCursorSelection(e.target as HTMLInputElement);
  }

  @HostListener('mouseup')
  @HostListener('keyup')
  @HostListener('touchend')
  onInteractionEnd(): void {
    this.trackCursor();
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent): void {
    const { data, inputType } = event;

    const element = event.target as HTMLInputElement;

    // Get the value from the input
    const inputValue = element.value;
    // Transform the input value in the actual value, or just use as is
    const value = this.valueTransform.reverse
      ? this.valueTransform.reverse(inputValue)
      : inputValue;
    // Transform the actual value to the display value
    const displayValue = this.valueTransform.transform(value);

    // Store values that we need for the `updateCursor` method
    const cursor = this.getCursorSelection(element);
    const previousValue = this._value;

    // Set the actual values
    element.value = displayValue;
    this.value = value;

    // Finally, update the cursor position
    this.updateCursor(
      {
        valueTransform: this.valueTransform,
        inputType,
        data,
        displayValue,
        current: {
          value,
          cursor,
        },
        previous: {
          value: previousValue,
          cursor: this.previousCursor,
        },
      },
      element,
    );
  }

  /**
   * Updates the cursor selection information on the given `element`.
   * In case a `updateCursor` function is provided, it is called with the
   * given `options.`
   *
   * @param options Options for updating the cursor
   * @param element The target input element on which the cursor selection information should be applied
   */
  private updateCursor(
    options: UpdateCursorOptions<string, unknown>,
    element: HTMLInputElement,
  ) {
    let {
      current: { cursor },
    } = options;

    // If an update function was passed, call it
    const { updateCursor } = this.valueTransform;
    if (updateCursor) {
      cursor = updateCursor(options);
      if (cursor) {
        // Make sure to have values that make sense
        cursor.start = clamp(0, element.value.length, cursor.start);
        cursor.end = clamp(0, element.value.length, cursor.end);
      }
    }
    this.setCursor(cursor, element);
  }

  /**
   * Tracks the current cursor selection
   */
  private trackCursor() {
    const {
      element: { nativeElement },
      valueTransform: { updateCursor },
    } = this;
    const cursor = this.getCursorSelection(nativeElement);

    if (updateCursor) {
      // If the transformer defines an update function, we call this to
      // let them handle the cursor position
      this.updateCursor(
        {
          valueTransform: this.valueTransform,
          inputType: 'none',
          data: '',
          displayValue: nativeElement.value,
          current: {
            value: this._value,
            cursor,
          },
        },
        nativeElement,
      );
    } else {
      // ...otherwise, we just store the latest selection
      this.previousCursor = cursor;
    }
  }

  /**
   * Sets the cursor or selection on the given `element`.
   *
   * @param cursor Cursor selection information
   * @param element The input element
   */
  private setCursor(cursor: CursorSelection | null, element: HTMLInputElement) {
    if (cursor) {
      element.selectionStart = cursor.start;
      element.selectionEnd = cursor.end;
    }

    this.previousCursor = cursor;
  }

  /**
   * @returns
   * Cursor selection information for an input element
   */
  private getCursorSelection(
    element: HTMLInputElement,
  ): CursorSelection | null {
    const { selectionStart: start, selectionEnd: end } = element;

    return start !== null && end !== null ? { start, end } : null;
  }

  /**
   * Updates the display value from the current value
   */
  private updateDisplayValue() {
    const { element } = this;
    if (element && element.nativeElement) {
      const displayValue = this.valueTransform.transform(this._value);
      element.nativeElement.value = displayValue;
    }
  }
}
